import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Eye, Loader2, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/compensation` (v0.4 章⑤ F-4、D-12/D-24/D-49)。
 * 3タブ: 賃金基準モード (実額/標準、ノード継承) / 標準単価区分 (該当1名警告) / 手当種別。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-3 / §5-6 / §5-10
 */

const STANDARD_RATE_USAGE_POINT_ID = 'standard_rate_tier';
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
// ⚠️ design-expert Critical指摘: type="number" はネイティブスピナーを抑制すること
// (NotificationsSettingsPage.tsx/Agreement36SettingsPage.tsx/VisitorCountsPage.tsx で確立済みのパターン)
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

function NodeSelector({ nodeId, onChange, label = '対象ノード' }: { nodeId: string; onChange: (id: string) => void; label?: string }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">{label}</label>
      <Select value={nodeId || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
        <SelectContent>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ1: 賃金基準モード (wage_basis)
// -----------------------------------------------------------------------------
const SAVE_SCOPE = { node: 'node', tenant: 'tenant' } as const;
type SaveScope = typeof SAVE_SCOPE[keyof typeof SAVE_SCOPE];

type ResolvedWageBasis = {
  value: 'actual' | 'standard' | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null;
};

// ⚠️ TD-22: このタブは守りが異なる2つのエンドポイントを持つ。
//   canManage             = PUT /settings/wage-basis (node_id 単位判定、own_store でも自店は可)
//   canManageAllocation   = PUT /settings/labor-cost-allocation (無条件 requireTenantWideManage)
function WageBasisTab({ canManageWageBasis, canManageAllocation }: { canManageWageBasis: boolean; canManageAllocation: boolean }) {
  const qc = useQueryClient();
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const rootId = nodesData?.roots[0]?.node_id ?? '';
  const [nodeId, setNodeId] = useState('');
  const effectiveNodeId = nodeId || rootId;
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);

  const { data, isLoading } = useQuery<ResolvedWageBasis>({
    queryKey: ['wage-basis', effectiveNodeId],
    queryFn: () => apiFetch<ResolvedWageBasis>(`/inheritable-settings/wage_basis/resolve?node_id=${encodeURIComponent(effectiveNodeId)}`),
    enabled: !!effectiveNodeId,
  });

  useEffect(() => {
    if (!data) return;
    setScope(data.source_kind === 'self' ? SAVE_SCOPE.node : SAVE_SCOPE.tenant);
  }, [data?.source_kind]);

  const currentValue = data?.value ?? 'standard';

  const saveMut = useMutation({
    mutationFn: (value: 'actual' | 'standard') => apiFetch('/settings/wage-basis', {
      method: 'PUT',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : effectiveNodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wage-basis'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub">
        人件費速報 (シフトから見込む人件費) の計算に使う単価の基準を選びます。<strong className="text-text-body">標準単価</strong> は
        役職・雇用形態等の区分ごとの丸めた単価で、少人数店舗でも個人の実際の時給が推測されにくい既定の安全な設定です。
        <strong className="text-text-body">実額</strong> にすると各人の実際の時給・給与で速報を算出します (年収の壁の見込みは常に実額を使うため、この設定の影響を受けません)。
      </p>
      <NodeSelector nodeId={nodeId} onChange={(id) => { setNodeId(id); }} label="対象ノード (実効値の確認元)" />
      {!effectiveNodeId ? (
        <p className="text-sm text-text-sub">対象ノードを選んでください。</p>
      ) : isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-3">
          <InheritanceSourceBadge sourceKind={data?.source_kind ?? 'none'} sourceNodeLabel={data?.source_node_label} />
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="賃金基準">
            {([['standard', '標準単価'], ['actual', '実額']] as const).map(([v, label]) => (
              <button key={v} type="button" aria-pressed={currentValue === v} disabled={!canManageWageBasis || saveMut.isPending}
                onClick={() => saveMut.mutate(v)}
                className={`px-4 min-h-[44px] text-sm font-medium disabled:opacity-45 ${currentValue === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
          {canManageWageBasis && (
            <div className="space-y-1.5">
              <label className="block text-xs text-text-sub">保存範囲</label>
              <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="保存範囲">
                {([[SAVE_SCOPE.node, 'このノードのみ'], [SAVE_SCOPE.tenant, 'テナント全体の既定']] as const).map(([v, label]) => (
                  <button key={v} type="button" aria-pressed={scope === v} onClick={() => setScope(v)}
                    className={`px-3 min-h-[44px] sm:min-h-[32px] text-xs font-medium ${scope === v ? 'bg-misespark-navy text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="border-t border-surface-border pt-4">
        <LaborCostAllocationPanel canManage={canManageAllocation} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 応援時の人件費計上先 (labor_cost_allocation、D-14/D-340)
// ⚠️ v0.4 追加要件⑩ D-340 (Q-231/Q-232): `default_override_mode='inherit_only'` のため
// wage_basis と異なりノード別上書きは無く、テナント全体で1本の値のみ。本社勤務者の応援分も
// 他の応援と同じこの1本の設定に従う (本社勤務者だけの別扱いは設けない、Q-231 回答)。
// 併せて `labor-cost-bridge.ts` の修正により、home_node を選んだ場合は単価解決も所属ノード
// (本部ノード) 基準になるため、本部ノードの賃金基準モードを「実額」にすれば本人の雇用条件が
// そのまま人件費速報に使われる (Q-232 回答)。
// -----------------------------------------------------------------------------
type ResolvedLaborCostAllocation = {
  value: 'work_node' | 'home_node' | null;
  source_kind: InheritanceSourceKind; source_node_label: string | null;
};

function LaborCostAllocationPanel({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const rootId = nodesData?.roots[0]?.node_id ?? '';

  const { data, isLoading } = useQuery<ResolvedLaborCostAllocation>({
    queryKey: ['labor-cost-allocation', rootId],
    queryFn: () => apiFetch<ResolvedLaborCostAllocation>(`/inheritable-settings/labor_cost_allocation/resolve?node_id=${encodeURIComponent(rootId)}`),
    enabled: !!rootId,
  });

  const currentValue = data?.value ?? 'work_node';

  const saveMut = useMutation({
    mutationFn: (value: 'work_node' | 'home_node') => apiFetch('/settings/labor-cost-allocation', {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['labor-cost-allocation'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-text-body">応援時の人件費計上先</h3>
        <p className="text-sm text-text-sub mt-1">
          他店舗や本社から応援に入った人の人件費速報を、<strong className="text-text-body">就業店</strong> (実際に働いた店舗) と
          <strong className="text-text-body">所属店</strong> (元々の所属先。本社勤務者の場合は本部) のどちらに計上するかを選びます。
          テナント全体で1つの設定です (店舗ごとの上書きはできません)。
        </p>
      </div>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-2">
          <InheritanceSourceBadge sourceKind={data?.source_kind ?? 'none'} sourceNodeLabel={data?.source_node_label} />
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="人件費計上先">
            {([['work_node', '就業店'], ['home_node', '所属店']] as const).map(([v, label]) => (
              <button key={v} type="button" aria-pressed={currentValue === v} disabled={!canManage || saveMut.isPending}
                onClick={() => saveMut.mutate(v)}
                className={`px-4 min-h-[44px] text-sm font-medium disabled:opacity-45 ${currentValue === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ2: 標準単価区分
// -----------------------------------------------------------------------------
type RateTier = {
  tier_id: string; node_id: string | null; condition_set_id: string; pay_unit: 'hourly' | 'daily' | 'monthly';
  amount: number; sort_order: number; source: string; label: string | null;
};
const PAY_UNIT_LABELS: Record<string, string> = { hourly: '時給', daily: '日給', monthly: '月給' };

type TierDraft = { tier_id: string | null; node_id: string | null; condition_set_id: string | null; pay_unit: 'hourly' | 'daily' | 'monthly'; amount: string; label: string };
const emptyTierDraft = (): TierDraft => ({ tier_id: null, node_id: null, condition_set_id: null, pay_unit: 'hourly', amount: '1000', label: '' });

function TierModal({ draft, setDraft, conditionSets, nextSortOrder, onClose, onSaved }: {
  draft: TierDraft; setDraft: (d: TierDraft) => void;
  conditionSets: { set_id: string; label: string | null }[]; nextSortOrder: number;
  onClose: () => void; onSaved: () => void;
}) {
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const previewMut = useMutation({
    mutationFn: () => apiFetch<{ matching_employee_count: number; single_employee_warning: boolean }>('/settings/standard-rates/preview', {
      method: 'POST', body: JSON.stringify({ condition_set_id: draft.condition_set_id }),
    }),
    onSuccess: (r) => setPreviewCount(r.matching_employee_count),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        node_id: draft.node_id, condition_set_id: draft.condition_set_id, pay_unit: draft.pay_unit,
        amount: Number(draft.amount) || 0, sort_order: draft.tier_id ? undefined : nextSortOrder,
        label: draft.label.trim() || null,
      };
      return draft.tier_id
        ? apiFetch(`/settings/standard-rates/${draft.tier_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/standard-rates', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = !!draft.condition_set_id && Number(draft.amount) > 0 && (draft.node_id === null || draft.node_id !== '');

  return (
    <Modal open title={draft.tier_id ? '標準単価区分を編集' : '標準単価区分を追加'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">適用範囲</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="適用範囲">
            {([[null, 'テナント全体の既定'], ['__node__', '特定のノード']] as const).map(([v, label]) => (
              <button key={label} type="button" aria-pressed={v === null ? draft.node_id === null : draft.node_id !== null}
                onClick={() => setDraft({ ...draft, node_id: v === null ? null : (draft.node_id ?? '') })}
                className={`px-3 min-h-[44px] text-xs font-medium ${(v === null ? draft.node_id === null : draft.node_id !== null) ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {draft.node_id !== null && (
          <NodeSelector nodeId={draft.node_id} onChange={(id) => setDraft({ ...draft, node_id: id })} />
        )}
        <div>
          <label className="block text-sm text-text-sub mb-1">対象者条件</label>
          <Select value={draft.condition_set_id ?? undefined} onValueChange={(v) => setDraft({ ...draft, condition_set_id: v })}>
            <SelectTrigger aria-label="対象者条件"><SelectValue placeholder="条件セットを選ぶ" /></SelectTrigger>
            <SelectContent>
              {conditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
            </SelectContent>
          </Select>
          {conditionSets.length === 0 && (
            <p className="text-xs text-alert-text mt-1">条件セットがまだありません。この画面下部の「対象者条件を管理」から先に作成してください。</p>
          )}
        </div>
        {draft.condition_set_id && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" leftIcon={previewMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>該当件数を確認</Button>
            {previewCount !== null && (
              <p className="text-xs flex items-start gap-1" aria-live="polite">
                <span>現在 <span className="font-semibold text-text-body tabular-nums">{previewCount}</span> 名が該当します</span>
                {previewCount === 1 && (
                  <span className="inline-flex items-start gap-1 text-alert-text font-semibold">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                    該当者が1名のため、この区分の単価が実質的にその1名の実額になります
                  </span>
                )}
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">単位</label>
            <Select value={draft.pay_unit} onValueChange={(v) => setDraft({ ...draft, pay_unit: v as TierDraft['pay_unit'] })}>
              <SelectTrigger aria-label="単位"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PAY_UNIT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">金額 (円)</label>
            <input type="number" min={0} value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className={numberInputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">ラベル (任意)</label>
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100} className={inputCls} />
        </div>
      </div>
    </Modal>
  );
}

function StandardRatesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<TierDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RateTier | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);

  const { data: tiers, isLoading } = useQuery<RateTier[]>({ queryKey: ['standard-rates'], queryFn: () => apiFetch<RateTier[]>('/settings/standard-rates') });
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const nodeNameById = useMemo(() => new Map(flattenNodes(nodesData?.roots ?? []).map(({ node }) => [node.node_id, node.name])), [nodesData]);

  const { data: catalog } = useQuery<ConditionCatalog>({ queryKey: ['condition-catalog'], queryFn: () => apiFetch('/condition-catalog'), staleTime: 5 * 60 * 1000 });
  const { data: setsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', STANDARD_RATE_USAGE_POINT_ID],
    queryFn: () => apiFetch(`/condition-sets?usage_point_id=${STANDARD_RATE_USAGE_POINT_ID}`),
  });
  const usagePoint = catalog?.usage_points.find((u) => u.usage_point_id === STANDARD_RATE_USAGE_POINT_ID);
  const attributeTypesById = useMemo(() => new Map((catalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t])), [catalog]);
  const conditionSets = useMemo(() => (setsData?.sets ?? []).map((s) => ({ set_id: s.set_id, label: s.label })), [setsData]);
  const setLabel = (id: string) => conditionSets.find((s) => s.set_id === id)?.label ?? id;

  const deleteMut = useMutation({
    mutationFn: (tierId: string) => apiFetch(`/settings/standard-rates/${tierId}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['standard-rates'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const sorted = [...(tiers ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const nextSortOrder = sorted.length > 0 ? Math.max(...sorted.map((t) => t.sort_order)) + 1 : 0;

  // ⚠️ standard_rate_tiers も (tenant_id, node_id, sort_order) に部分UNIQUE制約を持つ (migration 0051)。
  // VisibilityRulesSettingsPage.tsx の move と同じ理由で、一時退避値を経由する3段階の逐次PUTにする
  // (design-and-code MEDIUM指摘: 標準単価区分に並び替えUIが無く削除・再作成でしか順序変更できなかった)。
  // ⚠️ 一時退避値には sort_order.min(0) を満たさない -1 ではなく、全区分中の最大値+1 (nextSortOrder、
  // どの node_id とも衝突しない) を使う (F-2〜F-5 staging実機検証で -1 が常に400になる実装ミスと判明)。
  const move = async (tier: RateTier, direction: -1 | 1) => {
    const idx = sorted.findIndex((t) => t.tier_id === tier.tier_id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const target = sorted[targetIdx]!;
    try {
      await apiFetch(`/settings/standard-rates/${target.tier_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: nextSortOrder }) });
      await apiFetch(`/settings/standard-rates/${tier.tier_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: target.sort_order }) });
      await apiFetch(`/settings/standard-rates/${target.tier_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: tier.sort_order }) });
      qc.invalidateQueries({ queryKey: ['standard-rates'] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '並び替えに失敗しました');
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">
        対象者条件に一致した最初の区分の単価が採用されます (上から順に評価)。テナント全体の既定に加え、店舗ごとの上書きも設定できます。
      </p>

      {usagePoint && (
        <div className="border border-surface-border rounded-lg bg-white">
          <button type="button" onClick={() => setShowConditionEditor((v) => !v)} aria-expanded={showConditionEditor}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm font-semibold text-text-body">
            対象者条件を管理
          </button>
          {showConditionEditor && (
            <div className="px-3 pb-4">
              <ConditionSetEditor usagePointId={STANDARD_RATE_USAGE_POINT_ID} usagePoint={usagePoint} attributeTypesById={attributeTypesById} />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end">
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyTierDraft())}>区分を追加</Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : sorted.length === 0 ? (
        <EmptyState message="標準単価区分がまだ設定されていません" description="未設定の間、標準単価による人件費速報は算出されません。" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-surface-border text-left text-text-sub">
                {canManage && <th scope="col" className="py-2 pr-3" />}
                <th scope="col" className="py-2 pr-3">対象</th>
                <th scope="col" className="py-2 pr-3">条件</th>
                <th scope="col" className="py-2 pr-3">単位</th>
                <th scope="col" className="py-2 pr-3">金額</th>
                {canManage && <th scope="col" className="py-2 pr-3" />}
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, i) => (
                <tr key={t.tier_id} className="border-b border-surface-line">
                  {canManage && (
                    <td className="py-2 pr-3">
                      <div className="flex flex-col shrink-0">
                        <button type="button" aria-label="上へ" disabled={i === 0} onClick={() => move(t, -1)}
                          className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button type="button" aria-label="下へ" disabled={i === sorted.length - 1} onClick={() => move(t, 1)}
                          className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronDown size={14} /></button>
                      </div>
                    </td>
                  )}
                  <td className="py-2 pr-3">{t.node_id ? (nodeNameById.get(t.node_id) ?? t.node_id) : <span className="text-text-sub">テナント全体</span>}</td>
                  <td className="py-2 pr-3">{setLabel(t.condition_set_id)}{t.label && ` (${t.label})`}</td>
                  <td className="py-2 pr-3">{PAY_UNIT_LABELS[t.pay_unit]}</td>
                  <td className="py-2 pr-3 tabular-nums">{t.amount.toLocaleString('ja-JP')}円</td>
                  {canManage && (
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDraft({
                          tier_id: t.tier_id, node_id: t.node_id, condition_set_id: t.condition_set_id,
                          pay_unit: t.pay_unit, amount: String(t.amount), label: t.label ?? '',
                        })}>編集</Button>
                        <button type="button" aria-label="削除" onClick={() => setDeleteTarget(t)}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <TierModal draft={draft} setDraft={setDraft} conditionSets={conditionSets} nextSortOrder={nextSortOrder}
          onClose={() => setDraft(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['standard-rates'] }); setDraft(null); }} />
      )}

      <ConfirmDialog open={!!deleteTarget} title="標準単価区分を削除" message="この区分を削除します。この操作は取り消せません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.tier_id)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ3: 手当種別
// -----------------------------------------------------------------------------
type Allowance = {
  allowance_type_id: string; code: string; label: string; calc_basis: 'fixed' | 'per_workday' | 'per_hour';
  amount: number; is_included_in_overtime_base: boolean; condition_set_id: string | null;
};
const CALC_BASIS_LABELS: Record<string, string> = { fixed: '定額', per_workday: '出勤日数連動', per_hour: '時間連動' };

type AllowanceDraft = {
  allowance_type_id: string | null; code: string; label: string; calc_basis: 'fixed' | 'per_workday' | 'per_hour';
  amount: string; is_included_in_overtime_base: boolean;
};
const emptyAllowanceDraft = (): AllowanceDraft => ({ allowance_type_id: null, code: '', label: '', calc_basis: 'fixed', amount: '0', is_included_in_overtime_base: true });

function AllowanceModal({ draft, setDraft, onClose, onSaved }: { draft: AllowanceDraft; setDraft: (d: AllowanceDraft) => void; onClose: () => void; onSaved: () => void }) {
  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        code: draft.code.trim(), label: draft.label.trim(), calc_basis: draft.calc_basis,
        amount: Number(draft.amount) || 0, is_included_in_overtime_base: draft.is_included_in_overtime_base,
      };
      return draft.allowance_type_id
        ? apiFetch(`/settings/allowances/${draft.allowance_type_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/allowances', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const canSave = draft.label.trim() !== '' && (draft.allowance_type_id !== null || draft.code.trim() !== '');

  return (
    <Modal open title={draft.allowance_type_id ? '手当種別を編集' : '手当種別を追加'} onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        {!draft.allowance_type_id && (
          <div>
            <label className="block text-sm text-text-sub mb-1">コード</label>
            <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: night_shift" />
          </div>
        )}
        <div>
          <label className="block text-sm text-text-sub mb-1">名称</label>
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100} className={inputCls} placeholder="例: 賄い手当" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">算出方法</label>
            <Select value={draft.calc_basis} onValueChange={(v) => setDraft({ ...draft, calc_basis: v as AllowanceDraft['calc_basis'] })}>
              <SelectTrigger aria-label="算出方法"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CALC_BASIS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">金額 (円)</label>
            <input type="number" min={0} value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className={numberInputCls} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-text-body">
          <input type="checkbox" checked={draft.is_included_in_overtime_base} onChange={(e) => setDraft({ ...draft, is_included_in_overtime_base: e.target.checked })} />
          割増賃金の算定基礎に含める
        </label>
        <p className="text-xs text-text-sub">
          出勤日数連動・定額の手当は労基法施行規則21条の除外7項目に該当しない可能性が高いため、既定は「含める」(安全側) にしています。
        </p>
      </div>
    </Modal>
  );
}

function AllowancesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<AllowanceDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Allowance | null>(null);
  const { data: allowances, isLoading } = useQuery<Allowance[]>({ queryKey: ['allowances'], queryFn: () => apiFetch<Allowance[]>('/settings/allowances') });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/settings/allowances/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allowances'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        {canManage && <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyAllowanceDraft())}>手当種別を追加</Button>}
      </div>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (allowances ?? []).length === 0 ? (
        <EmptyState message="手当種別がまだ登録されていません" description="人件費計算に手当を含める場合はここで登録してください。" />
      ) : (
        <ul className="space-y-2">
          {(allowances ?? []).map((a) => (
            <li key={a.allowance_type_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body">{a.label}</p>
                <p className="text-xs text-text-sub mt-0.5">
                  {CALC_BASIS_LABELS[a.calc_basis]} ・ {a.amount.toLocaleString('ja-JP')}円 ・ 割増算定基礎: {a.is_included_in_overtime_base ? '含める' : '含めない'}
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setDraft({
                    allowance_type_id: a.allowance_type_id, code: a.code, label: a.label, calc_basis: a.calc_basis,
                    amount: String(a.amount), is_included_in_overtime_base: a.is_included_in_overtime_base,
                  })}>編集</Button>
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(a)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {draft && (
        <AllowanceModal draft={draft} setDraft={setDraft} onClose={() => setDraft(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['allowances'] }); setDraft(null); }} />
      )}
      <ConfirmDialog open={!!deleteTarget} title="手当種別を削除" message="この手当種別を削除します。この操作は取り消せません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.allowance_type_id)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ4: 処遇テーブル (competency_pay_policies、E14。増分⑥ D-704)
// ⚠️ D-694 の教訓 (ui-guidelines.md §2.2「赤は1画面1箇所」): このタブ内の選択状態には brand-red を
// 使わない (このページ内で既に「賃金基準モード」「標準単価区分」タブがトグルの選択状態に brand-red を
// 使っている)。WageBasisTab の「保存範囲」トグルと同じ misespark-navy を使う。
// -----------------------------------------------------------------------------
type RoleProfileOption = { role_profile_id: string; label: string };
type EmploymentTypeOption = { type_id: string; label: string };
type PayPolicy = {
  policy_id: string; role_profile_id: string; role_profile_label: string;
  employment_type_id: string | null; node_id: string | null;
  base_amount: number | null; pay_unit: 'hourly' | 'daily' | 'monthly' | null;
  increment_unit: string; increment_amount: number | null; currency_code: string;
};
type PayPolicyDraft = {
  policy_id: string | null; role_profile_id: string | null; employment_type_id: string | null; node_id: string | null;
  base_amount: string; pay_unit: 'hourly' | 'daily' | 'monthly'; increment_amount: string;
};
const emptyPayPolicyDraft = (): PayPolicyDraft => ({
  policy_id: null, role_profile_id: null, employment_type_id: null, node_id: null,
  base_amount: '', pay_unit: 'hourly', increment_amount: '',
});

function PayPolicyModal({ draft, setDraft, roleProfiles, employmentTypes, onClose, onSaved }: {
  draft: PayPolicyDraft; setDraft: (d: PayPolicyDraft) => void;
  roleProfiles: RoleProfileOption[]; employmentTypes: EmploymentTypeOption[];
  onClose: () => void; onSaved: () => void;
}) {
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const previewMut = useMutation({
    mutationFn: () => apiFetch<{ matching_employee_count: number; single_employee_warning: boolean }>('/settings/pay-policies/preview', {
      method: 'POST',
      body: JSON.stringify({ role_profile_id: draft.role_profile_id, employment_type_id: draft.employment_type_id, node_id: draft.node_id }),
    }),
    onSuccess: (r) => setPreviewCount(r.matching_employee_count),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        role_profile_id: draft.role_profile_id, employment_type_id: draft.employment_type_id, node_id: draft.node_id,
        base_amount: draft.base_amount.trim() === '' ? null : Number(draft.base_amount),
        pay_unit: draft.base_amount.trim() === '' ? null : draft.pay_unit,
        increment_unit: 'face' as const,
        increment_amount: draft.increment_amount.trim() === '' ? null : Number(draft.increment_amount),
      };
      return draft.policy_id
        ? apiFetch(`/settings/pay-policies/${draft.policy_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/pay-policies', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = !!draft.role_profile_id;

  return (
    <Modal open title={draft.policy_id ? '処遇テーブルの設定を編集' : '処遇テーブルの設定を追加'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">対象の人材像 (役割)</label>
          <Select value={draft.role_profile_id ?? undefined} onValueChange={(v) => { setDraft({ ...draft, role_profile_id: v }); setPreviewCount(null); }}>
            <SelectTrigger aria-label="対象の人材像"><SelectValue placeholder="人材像を選ぶ" /></SelectTrigger>
            <SelectContent>
              {roleProfiles.map((r) => <SelectItem key={r.role_profile_id} value={r.role_profile_id}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {roleProfiles.length === 0 && (
            <p className="text-xs text-alert-text mt-1">人材像がまだ登録されていません。先に人材像マトリクスで登録してください。</p>
          )}
        </div>

        <div>
          <label className="block text-sm text-text-sub mb-1">対象の雇用形態 (任意)</label>
          <Select value={draft.employment_type_id ?? '__all__'} onValueChange={(v) => setDraft({ ...draft, employment_type_id: v === '__all__' ? null : v })}>
            <SelectTrigger aria-label="対象の雇用形態"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全ての雇用形態</SelectItem>
              {employmentTypes.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm text-text-sub mb-1">適用範囲</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="適用範囲">
            {([[null, 'テナント全体の既定'], ['__node__', '特定のノード']] as const).map(([v, label]) => (
              <button key={label} type="button" aria-pressed={v === null ? draft.node_id === null : draft.node_id !== null}
                onClick={() => setDraft({ ...draft, node_id: v === null ? null : (draft.node_id ?? '') })}
                className={`px-3 min-h-[44px] text-xs font-medium ${(v === null ? draft.node_id === null : draft.node_id !== null) ? 'bg-misespark-navy text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {draft.node_id !== null && (
          <NodeSelector nodeId={draft.node_id} onChange={(id) => setDraft({ ...draft, node_id: id })} />
        )}

        {draft.role_profile_id && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" leftIcon={previewMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>該当件数を確認</Button>
            {previewCount !== null && (
              <p className="text-xs flex items-start gap-1" aria-live="polite">
                <span>現在 <span className="font-semibold text-text-body tabular-nums">{previewCount}</span> 名が該当します</span>
                {previewCount === 1 && (
                  <span className="inline-flex items-start gap-1 text-alert-text font-semibold">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                    該当者が1名のため、この設定が実質的にその1名限定の処遇になります
                  </span>
                )}
              </p>
            )}
          </div>
        )}

        <div className="border-t border-surface-border pt-4 space-y-3">
          <p className="text-xs text-text-sub">
            金額は未入力のままでも他の機能に影響しません (この役割に到達した事実だけを記録し、後から金額を入力できます)。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">基準額 (円、任意)</label>
              <input type="number" min={0} value={draft.base_amount} onChange={(e) => setDraft({ ...draft, base_amount: e.target.value })} className={numberInputCls} placeholder="未入力可" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">単位 (基準額を入力した場合は必須)</label>
              <Select value={draft.pay_unit} onValueChange={(v) => setDraft({ ...draft, pay_unit: v as PayPolicyDraft['pay_unit'] })}>
                <SelectTrigger aria-label="単位"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PAY_UNIT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">必要な面 (スキル) を満たすごとの加算額 (円、任意)</label>
            <input type="number" min={0} value={draft.increment_amount} onChange={(e) => setDraft({ ...draft, increment_amount: e.target.value })} className={numberInputCls} placeholder="未入力可" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function PayPoliciesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PayPolicyDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PayPolicy | null>(null);

  const { data: policies, isLoading } = useQuery<PayPolicy[]>({ queryKey: ['pay-policies'], queryFn: () => apiFetch<PayPolicy[]>('/settings/pay-policies') });
  const { data: roleProfilesData } = useQuery<Array<{ role_profile_id: string; label: string }>>({
    queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles'),
  });
  const roleProfiles: RoleProfileOption[] = (roleProfilesData ?? []).map((r) => ({ role_profile_id: r.role_profile_id, label: r.label }));
  const { data: employmentTypesData } = useQuery<Array<{ type_id: string; label: string }>>({
    queryKey: ['employment-types'], queryFn: () => apiFetch('/employment-types'),
  });
  const employmentTypes: EmploymentTypeOption[] = (employmentTypesData ?? []).map((t) => ({ type_id: t.type_id, label: t.label }));
  const employmentTypeLabelById = useMemo(() => new Map(employmentTypes.map((t) => [t.type_id, t.label])), [employmentTypes]);
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const nodeNameById = useMemo(() => new Map(flattenNodes(nodesData?.roots ?? []).map(({ node }) => [node.node_id, node.name])), [nodesData]);

  const deleteMut = useMutation({
    mutationFn: (policyId: string) => apiFetch(`/settings/pay-policies/${policyId}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pay-policies'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const rows = policies ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">
        人材像 (役割) への到達に応じた給与の基準額、および必要な面 (スキル) を満たすごとの加算額を設定します。
        金額は未入力のままでも他の機能は正常に動作します。
      </p>

      <div className="flex items-center justify-end">
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyPayPolicyDraft())}>処遇を追加</Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : rows.length === 0 ? (
        <EmptyState message="処遇テーブルの設定がまだありません" description="未設定の間、役割到達・面の充足による給与への反映は行われません (標準単価のみが適用されます)。" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-surface-border text-left text-text-sub">
                <th scope="col" className="py-2 pr-3">人材像</th>
                <th scope="col" className="py-2 pr-3">雇用形態</th>
                <th scope="col" className="py-2 pr-3">対象</th>
                <th scope="col" className="py-2 pr-3">基準額</th>
                <th scope="col" className="py-2 pr-3">面の加算額</th>
                {canManage && <th scope="col" className="py-2 pr-3" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.policy_id} className="border-b border-surface-line">
                  <td className="py-2 pr-3">{p.role_profile_label}</td>
                  <td className="py-2 pr-3">{p.employment_type_id ? (employmentTypeLabelById.get(p.employment_type_id) ?? p.employment_type_id) : <span className="text-text-sub">全て</span>}</td>
                  <td className="py-2 pr-3">{p.node_id ? (nodeNameById.get(p.node_id) ?? p.node_id) : <span className="text-text-sub">テナント全体</span>}</td>
                  <td className="py-2 pr-3 tabular-nums">{p.base_amount != null ? `${p.base_amount.toLocaleString('ja-JP')}円 (${PAY_UNIT_LABELS[p.pay_unit ?? ''] ?? p.pay_unit})` : <span className="text-text-sub">未設定</span>}</td>
                  <td className="py-2 pr-3 tabular-nums">{p.increment_amount != null ? `+${p.increment_amount.toLocaleString('ja-JP')}円/面` : <span className="text-text-sub">未設定</span>}</td>
                  {canManage && (
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDraft({
                          policy_id: p.policy_id, role_profile_id: p.role_profile_id, employment_type_id: p.employment_type_id, node_id: p.node_id,
                          base_amount: p.base_amount != null ? String(p.base_amount) : '', pay_unit: p.pay_unit ?? 'hourly',
                          increment_amount: p.increment_amount != null ? String(p.increment_amount) : '',
                        })}>編集</Button>
                        <button type="button" aria-label="削除" onClick={() => setDeleteTarget(p)}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <PayPolicyModal draft={draft} setDraft={setDraft} roleProfiles={roleProfiles} employmentTypes={employmentTypes}
          onClose={() => setDraft(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pay-policies'] }); setDraft(null); }} />
      )}

      <ConfirmDialog open={!!deleteTarget}
        title="処遇テーブルの設定を削除"
        message={deleteTarget ? `「${deleteTarget.role_profile_label}」${deleteTarget.employment_type_id ? ` / ${employmentTypeLabelById.get(deleteTarget.employment_type_id) ?? deleteTarget.employment_type_id}` : ''} / ${deleteTarget.node_id ? (nodeNameById.get(deleteTarget.node_id) ?? deleteTarget.node_id) : 'テナント全体'} の処遇設定を削除します。この操作は取り消せません。` : ''}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.policy_id)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
export function CompensationSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  // ⚠️ compensation は NON_INHERITING_FEATURES 対象そのもの。継承遮断の正はサーバー側にあるため
  // Web のハードコード既定値ではなく /auth/me が返す non_inheriting_features を渡す (§8-93 対策)。
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'compensation', 'manage', user?.non_inheriting_features);
  // ⚠️ design-and-code HIGH指摘: 賃金基準モード (wage_basis) はバックエンド (PUT /settings/wage-basis)
  // が 'tenant_config' の manage 権限を要求する (標準単価区分・手当種別の 'compensation' とは別feature)。
  // 誤って canManage (compensation) を渡すと、compensationのみ持つロールで保存ボタンが有効に見えて
  // 常に403になる不整合が起きる。
  const canManageWageBasis = hasPermission(user?.effective_permissions, 'tenant_config', 'manage');
  // ⚠️ TD-22: 同じ wage-basis タブ内でも「人件費の計上先」(PUT /settings/labor-cost-allocation) だけは
  // node_id を取らず無条件で requireTenantWideManage を通すため、全店舗スコープが要る。
  const canManageAllocationTenantWide = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const [tab, setTab] = useState<'wage-basis' | 'standard-rates' | 'allowances' | 'pay-policies'>('wage-basis');

  // ⚠️ TD-22: 賃金基準モードは対象ノード単位判定、標準単価区分・手当種別・人件費計上先は
  // テナント全体を要求する。要件が違うので注記も分ける。
  // design-and-codeレビュー指摘: permsReady追加でネスト三項演算子が読みにくくなったため、
  // 表示する注記文をここで一本化してから描画側は単純な `{scopeNoteText && <p>...}` にする。
  const scopeNoteText: string | null = !permsReady
    ? null
    : tab === 'wage-basis'
      ? (!canManageWageBasis
        ? '閲覧のみ可能です (編集には対象ノードの管理権限が必要です)。'
        : (!canManageAllocationTenantWide
          ? '賃金基準モードは編集できますが、人件費の計上先の変更には全店舗の管理権限が必要です。'
          : null))
      : (!canManage ? '閲覧のみ可能です (編集にはテナント全体の管理権限が必要です)。' : null);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">給与単価設定</h2>
        <p className="text-sm text-text-sub mt-1">人件費速報に使う単価の基準・標準単価区分・手当種別を設定します。</p>
        {scopeNoteText && <p className="text-xs text-text-sub mt-1">{scopeNoteText}</p>}
      </div>
      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="タブ">
          {([['wage-basis', '賃金基準モード'], ['standard-rates', '標準単価区分'], ['allowances', '手当種別'], ['pay-policies', '処遇テーブル']] as const).map(([v, label]) => (
            <button key={v} type="button" onClick={() => setTab(v)} aria-current={tab === v}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === v ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {label}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'wage-basis' && <WageBasisTab canManageWageBasis={canManageWageBasis} canManageAllocation={canManageAllocationTenantWide} />}
      {tab === 'standard-rates' && <StandardRatesTab canManage={canManage} />}
      {tab === 'allowances' && <AllowancesTab canManage={canManage} />}
      {tab === 'pay-policies' && <PayPoliciesTab canManage={canManage} />}
    </div>
  );
}
